import { PaginatedQueryParams } from './api'
import { OrderingField } from './utility'

export enum CallStatus {
  Busy = 'busy',
  Canceled = 'canceled',
  Completed = 'completed',
  Failed = 'failed',
  InProgress = 'in-progress',
}

export enum CallType {
  BlockUnblockNumber = 'block/unblock number',
  CheckVoicemail = 'check voicemail',
  ConferencePage = 'conference page',
  DayNightToggle = 'day/night toggle',
  DirectPage = 'direct page',
  EmergencyAlert = 'emergency alert',
  EmergencyHistory = 'emergency history',
  Fax = 'fax',
  SetHotelCleanStatus = 'set clean status',
  SetHotelInspectedStatus = 'set inspected status',
  SetHotelStayStatus = 'set stay status',
  QueueLogin = 'queue: login',
  QueueLogout = 'queue: logout',
  CreateSoundClip = 'sound clip: create',
  UpdateSoundClip = 'sound clip: update',
  Standard = 'standard',
  WakeUp = 'wake-up',
  WakeUpRequest = 'wake-up request',
}

export enum CallDirection {
  Inbound = 'inbound',
  Outbound = 'outbound',
  Internal = 'internal',
}

export declare enum Sentiment {
  Negative = 'negative',
  Neutral = 'neutral',
  Positive = 'positive',
}

export interface CallSentiment {
  customer_overall_score?: number
  office_overall_score?: number
}
export interface CallPhoneNumber {
  id: number
  number?: string
  name?: string
  location?: string
}

export interface CallExtension {
  id: number
  number: string
  name: string
}

export interface CallLocation {
  id: number
  name: string
}

export interface CallUser {
  id: number
  name: string
}

export interface Caller {
  caller_id?: string
  formatted: string
  cnam_location?: string
  extension?: CallExtension
  phone_number?: CallPhoneNumber
  location?: CallLocation
  user?: CallUser
}

export interface Call {
  id: number
  company: {
    id: number
    name: string
  }
  pbx: {
    id: number
    name: string
  }
  status: CallStatus
  type: CallType
  direction: CallDirection
  from: Caller
  to: Caller
  is_missed: boolean
  duration_in_seconds: number
  started_at: string
  externally_transferred_at?: string
  voicemail_id?: number
  fax_id?: number
  transcript_id?: number
  ended_at: string
  created_at: string
  updated_at: string
  call_sentiment?: CallSentiment
  transcription_status?: string
}

export interface CallFlowEntry {
  label: string
  value: string
}
export interface CallInstance extends Call {
  first_answered_at?: string
  call_flow_entries?: CallFlowEntry[]
  recording_url?: string
  transcript?: string
}

export const stateParamToRouteParam = {
  directions: 'dir',
  duration_in_seconds_gte: 'dur_gte',
  duration_in_seconds_lt: 'dur_lt',
  statuses: 'status',
  has_voicemail: 'vmr',
  is_missed: 'miss',
  is_externally_transferred: 'trnsfr',
  related_user_ids: 'u',
  office_overall_sentiment_score: 'agent_sentiment',
  customer_overall_sentiment_score: 'customer_sentiment',
} as const
type CallFilterKeys = keyof typeof stateParamToRouteParam
export const callFilterKeys = Object.keys(
  stateParamToRouteParam,
) as CallFilterKeys[]
export interface CallFilterParams {
  customer_overall_sentiment_score?: Sentiment[]
  directions?: CallDirection[]
  duration_in_seconds_gte?: number
  duration_in_seconds_lt?: number
  has_voicemail?: boolean
  is_externally_transferred?: boolean
  is_missed?: boolean
  statuses?: CallStatus[]
  office_overall_sentiment_score?: Sentiment[]
  related_user_ids?: number[]
}

export type CallsQueryParams = PaginatedQueryParams &
  CallFilterParams & {
    company_ids?: number[]
    contact_ids?: number
    has_transcript?: boolean
    ordering?: OrderingField<Call>[]
    pbx_ids?: number[]
    relevant_phone_number_ids?: number[]
    search?: string
    started_after?: Date
    started_before?: Date
    types?: CallType[]
    t?: number
  }
